import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TaskManagement from '../task-management/components/TaskManagement';
import SQSQueueMonitoring from './SQSQueueMonitoring';
import WorkerMonitoring from './WorkerMonitoring';
import AggregateHealth from './AggregateHealth';
import ClientsAnalytics from './ClientsAnalytics';
import SentryTesting from '../../sentry-testing/components/SentryTesting';
import MessagingMonitoring from '../../messaging-monitoring/components/MessagingMonitoring';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import { useSelector } from 'react-redux';
import { isCurrentUserSuperUser } from 'common/selectors/auth';

import Tabs from 'common/components/Tabs';

const TAB_SLUGS = {
  jobs: 'Jobs',
  messages: 'Messaging',
  sqs: 'Queues',
  workers: 'Workers',
  aggregates: 'Aggregates',
  clients: 'Clients',
  errors: 'Errors',
} as const;

type TabSlug = keyof typeof TAB_SLUGS;

const slugFromLabel = (label: string): TabSlug =>
  (Object.entries(TAB_SLUGS).find(([, v]) => v === label)?.[0] as TabSlug) ?? 'jobs';

const SystemHealth: React.FC = () => {
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isSuperUser = useSelector(isCurrentUserSuperUser);

  const tabs = isSuperUser
    ? ['Jobs', 'Messaging', 'Queues', 'Workers', 'Aggregates', 'Clients', 'Errors']
    : ['Jobs', 'Messaging', 'Queues', 'Workers', 'Aggregates', 'Clients'];

  const defaultSlug: TabSlug = 'jobs';
  const activeSlug = (urlTab as TabSlug) in TAB_SLUGS ? (urlTab as TabSlug) : defaultSlug;
  const activeLabel = TAB_SLUGS[activeSlug];

  // Validate slug: redirect if missing, invalid, or pointing to a hidden tab
  useEffect(() => {
    const isHidden = activeSlug === 'errors' && !isSuperUser;

    if (!urlTab || !(urlTab in TAB_SLUGS) || isHidden) {
      navigate(`/settings/admin/system-health/${defaultSlug}`, { replace: true });
    }
  }, [urlTab, activeSlug, defaultSlug, isSuperUser, navigate]);

  const selectedIndex = tabs.indexOf(activeLabel);

  return (
    <PageContent data-testid="admin-system-health-page">
      <PageHeader
        data-testid="admin-system-health-header"
        title={`System Health`}
        divider
      />

      <PageSection>
        <Tabs
          data-testid="admin-system-health-tabs"
          options={tabs}
          onSelect={(idx: number) => {
            const slug = slugFromLabel(tabs[idx]);
            navigate(`/settings/admin/system-health/${slug}`);
          }}
          selected={selectedIndex >= 0 ? selectedIndex : 0}
          borderbottom
        />

        {activeLabel === 'Jobs' && (
          <PageSection padright>
            <TaskManagement />
          </PageSection>
        )}
        {activeLabel === 'Queues' && (
          <PageSection>
            <SQSQueueMonitoring />
          </PageSection>
        )}
        {activeLabel === 'Workers' && (
          <PageSection padtop>
            <WorkerMonitoring />
          </PageSection>
        )}
        {activeLabel === 'Aggregates' && (
          <PageSection padtop>
            <AggregateHealth />
          </PageSection>
        )}
        {activeLabel === 'Clients' && (
          <PageSection>
            <ClientsAnalytics />
          </PageSection>
        )}
        {activeLabel === 'Errors' && (
          <PageSection>
            <SentryTesting />
          </PageSection>
        )}

        {activeLabel === 'Messaging' && (
          <PageSection>
            <MessagingMonitoring />
          </PageSection>
        )}
      </PageSection>
    </PageContent>
  );
};

export default SystemHealth;
